import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { safeJsonParse } from '../../../utils/safeJsonParse';
import styles from './index.module.scss';
import { setRoutePathNames } from '../../../redux/slices/tabsDataSlice';
import { resetFilters } from '../../../redux/slices/currentTabSlice';

// Импорт всех страниц
import TabPage from '../../pages/tab_page';
import UnknownPage from '../../pages/unknown_page';
import PublicationPage from '../../pages/publication_page';
import UnknownPublicationPage from '../../pages/unknown_publication_page';
import OrderAccepted from '../../pages/order_accepted';

// Импорт компонентов
import CallbackBtn from '../../render_components/other_components/callback_btn';
import StaticPodheader from '../podheader_system/static_podheader';
import BackgroundPodheader from '../podheader_system/background_podheader';
import MobilePodheader from '../podheader_system/mobile_podheader';

const MAIN_OTHER_TEMPLATE_NAMES = ['callback_btn'];

const Main = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const tabOrPublicationPage = useSelector((state) => state.app.tab_or_publication_page);
  const tabsData = useSelector((state) => state.tabs_data.tabs);
  const others = useSelector((state) => state.others_data.others);
  const imgs_for_filters = useSelector((state) => state.others_data.imgs_for_filters);
  const skipFilterReset = useSelector((state) => state.currentTab.skipFilterReset);

  // useEffect(() => {
  //   console.log('imgs_for_filters', imgs_for_filters);
  // }, [imgs_for_filters]);
  const [isMobile, setIsMobile] = React.useState(false);
  const [othersToRender, setOthersToRender] = React.useState([]);

  // Явный рендер other-компонентов: перебор others по template_name
  useEffect(() => {
    if (!others || !Array.isArray(others)) {
      setOthersToRender([]);
      return;
    }
    const allowed = new Set(MAIN_OTHER_TEMPLATE_NAMES);
    const list = [];
    others.forEach((el) => {
      const template = safeJsonParse(el.template, {});
      const name = template?.template_name;
      if (name && allowed.has(name)) {
        list.push({
          template_name: name,
          contentData: safeJsonParse(el.content, []),
          path_to_folder: el.path && el.blank_name ? 'https://' + el.path + el.blank_name + '/' : null,
        });
      }
    });
    setOthersToRender(list);
  }, [others]);
  
  // Селекторы для получения данных о шаблонах
  const currentTab = useSelector((state) => state.currentTab.currentTab);
  const publications = useSelector((state) => state.currentTab.publications);
  const blankTemplate = useSelector((state) => state.currentTab.blank_template);
  const currentPublication = useSelector((state) => state.currentPublication.currentPublication);

  React.useEffect(() => {
    // Массив путей роутов без "/"
    const routePaths = ['', 'about_us', 'catalog', 'treatment', 'cart', 'contacts', 'unknown'];
    dispatch(setRoutePathNames(routePaths));
  }, []);

  // Определение мобильного режима
  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Определяем текущий раздел из URL
  const currentPath = location.pathname;
  const currentSection = currentPath === '/' ? 'main_tab' : currentPath.split('/')[1];

  // Ref для отслеживания предыдущего раздела
  const prevSectionRef = useRef(currentSection);

  // useEffect для сброса фильтров при смене раздела (но не при входе в публикацию)
  useEffect(() => {
    const prevSection = prevSectionRef.current;

    // Сбрасываем фильтры только если:
    // 1. Раздел изменился
    // 2. Не переходим в публикацию (tabOrPublicationPage !== 'publication')
    // 3. Не первый рендер (prevSection !== currentSection)
    // 4. Не установлен флаг пропуска сброса фильтров
    if (
      prevSection !== currentSection &&
      tabOrPublicationPage !== 'publication' &&
      !skipFilterReset
    ) {
      dispatch(resetFilters());
    }

    // Обновляем предыдущий раздел
    prevSectionRef.current = currentSection;
  }, [currentSection, tabOrPublicationPage, skipFilterReset, dispatch]);

  // Находим данные для podheader компонентов по тегам для текущего раздела
  const podheaderData =
    tabsData && Array.isArray(tabsData)
      ? tabsData.find((el) => {
          if (el.tab_tag && el.eng_name === currentSection) {
            const tagArray = el.tab_tag.includes(',') ? el.tab_tag.split(',') : [el.tab_tag];
            return tagArray.includes('podheader');
          }
          return false;
        })
      : null;

  const backgroundPodheaderData =
    tabsData && Array.isArray(tabsData)
      ? tabsData.find((el) => {
          if (el.tab_tag && el.eng_name === currentSection) {
            const tagArray = el.tab_tag.includes(',') ? el.tab_tag.split(',') : [el.tab_tag];
            return tagArray.includes('back_podheader');
          }
          return false;
        })
      : null;

  const shPodheaderData =
    tabsData && Array.isArray(tabsData)
      ? tabsData.find((el) => {
          if (el.tab_tag && el.eng_name === currentSection) {
            const tagArray = el.tab_tag.includes(',') ? el.tab_tag.split(',') : [el.tab_tag];
            return tagArray.includes('sh_podheader');
          }
          return false;
        })
      : null;

  // Диагностика отступов подхедеров
  //   'podheader (тег: podheader)': !!podheaderData,
  //   'background (тег: back_podheader)': !!backgroundPodheaderData,
  //   'shPodheader (тег: sh_podheader)': !!shPodheaderData,
  // });
  //
  // if (podheaderData) {
  // }
  // if (shPodheaderData) {
  // }
  // if (backgroundPodheaderData) {
  // }

  // Детальное логирование tabsData для отладки
  // if (tabsData && Array.isArray(tabsData)) {
  //   tabsData.forEach((tab, index) => {
  //       eng_name: tab.eng_name,
  //       tab_tag: tab.tab_tag,
  //       hasPodheaderTag:
  //         tab.tab_tag &&
  //         (tab.tab_tag.includes('podheader') ||
  //           tab.tab_tag.includes('back_podheader') ||
  //           tab.tab_tag.includes('sh_podheader')),
  //     });
  //   });
  // }

  // Сбор и вывод всех имен шаблонов текущей страницы
  React.useEffect(() => {
    // const templateNames = [];

    if (tabOrPublicationPage === 'tab') {
      // Для страницы с вкладкой собираем шаблоны из публикаций
      if (publications && Array.isArray(publications)) {
        publications.forEach((publication) => {
          const templateName = publication?.template?.template_name;
          // if (templateName) {
          //   templateNames.push(templateName);
          // }
          
          // Проверка: если шаблон == 'reviews', выводим content записи как объект
          if (templateName === 'reviews') {
            const content = publication?.content;
            const contentObject = typeof content === 'string' 
              ? safeJsonParse(content, content) 
              : content;
            // console.log('=== Content записи с шаблоном "reviews" ===');
            // console.log(contentObject);
            // console.log('==========================================');
          }
        });
      }
      
      // Также проверяем blank_template для страниц с blank шаблоном
      // if (blankTemplate) {
      //   templateNames.push(blankTemplate);
      // }
    } else if (tabOrPublicationPage === 'publication') {
      // Для страницы публикации берем шаблон из currentPublication
      const templateName = currentPublication?.template?.template_name;
      // if (templateName) {
      //   templateNames.push(templateName);
      // }
      
      // Проверка: если шаблон == 'reviews', выводим content записи как объект
      if (templateName === 'reviews') {
        const content = currentPublication?.content;
        const contentObject = typeof content === 'string' 
          ? safeJsonParse(content, content) 
          : content;
        // console.log('=== Content записи с шаблоном "reviews" ===');
        // console.log(contentObject);
        // console.log('==========================================');
      }
    }

    // Удаляем дубликаты и выводим в консоль
    // const uniqueTemplateNames = [...new Set(templateNames)];
    
    // if (uniqueTemplateNames.length > 0) {
    //   console.log('=== Имена шаблонов текущей страницы ===');
    //   console.log('Тип страницы:', tabOrPublicationPage);
    //   console.log('Имена шаблонов:', uniqueTemplateNames);
    //   console.log('Количество уникальных шаблонов:', uniqueTemplateNames.length);
    //   console.log('========================================');
    // }
  }, [tabOrPublicationPage, publications, blankTemplate, currentPublication]);

  return (
    <>
      {/* Podheader компоненты на основе тегов из tabsData - не показываем если открыта публикация */}
      {!isMobile ? (
        <>
          {podheaderData && tabOrPublicationPage !== 'publication' && (
            <StaticPodheader
              data={podheaderData}
              path={podheaderData.path + podheaderData.blank_name + '/'}
              currentSection={currentSection}
              tag={podheaderData.tab_tag}
            />
          )}

          {backgroundPodheaderData && (
            <BackgroundPodheader
              data={safeJsonParse(backgroundPodheaderData.content, {})}
              path={backgroundPodheaderData.path + backgroundPodheaderData.blank_name + '/'}
            />
          )}

          {shPodheaderData && tabOrPublicationPage !== 'publication' && (
            <StaticPodheader
              data={shPodheaderData}
              path={shPodheaderData.path + shPodheaderData.blank_name + '/'}
              currentSection={currentSection}
              tag={shPodheaderData.tab_tag}
            />
          )}
        </>
      ) : (
        /* В мобильном режиме показываем только background подхедер для фона */
        backgroundPodheaderData && (
          <BackgroundPodheader
            data={safeJsonParse(backgroundPodheaderData.content, {})}
            path={backgroundPodheaderData.path + backgroundPodheaderData.blank_name + '/'}
          />
        )
      )}

      <main className={styles.main}>
        {/* Контейнер для отображения текущей страницы */}
        <section className={styles.content}>
          {tabOrPublicationPage == 'tab' && (
            <Routes>
              <Route path='/' element={<TabPage />} />
              <Route path='/:pageNumber' element={<TabPage />} />
              <Route path='/about_us' element={<TabPage />} />
              <Route path='/about_us/:pageNumber' element={<TabPage />} />
              <Route path='/catalog' element={<TabPage />} />
              <Route path='/catalog/:pageNumber' element={<TabPage />} />
              <Route path='/info' element={<TabPage />} />
              <Route path='/info/:pageNumber' element={<TabPage />} />
              <Route path='/cart' element={<TabPage />} />
              <Route path='/cart/:pageNumber' element={<TabPage />} />
              <Route path='/contacts' element={<TabPage />} />
              <Route path='/contacts/:pageNumber' element={<TabPage />} />
              <Route path='/order_accepted' element={<OrderAccepted />} />
              <Route path='/*' element={<UnknownPage />} />
            </Routes>
          )}

          {tabOrPublicationPage == 'publication' && (
            <Routes>
              <Route path='/order_accepted' element={<OrderAccepted />} />
              <Route path='/:publicationId' element={<PublicationPage />} />
              <Route path='/about_us/:publicationId' element={<PublicationPage />} />
              <Route path='/catalog/:publicationId' element={<PublicationPage />} />
              <Route path='/info/:publicationId' element={<PublicationPage />} />
              <Route path='/cart/:publicationId' element={<PublicationPage />} />
              <Route path='/contacts/:publicationId' element={<PublicationPage />} />
              <Route path='/*' element={<UnknownPublicationPage />} />
            </Routes>
          )}
        </section>

        {/* Явный рендер other-компонентов Main */}
        {othersToRender.map((item, idx) => {
          switch (item.template_name) {
            case 'callback_btn':
              return (
                <CallbackBtn
                  key={idx}
                  contentData={item.contentData}
                  path_to_folder={item.path_to_folder}
                />
              );
            default:
              return null;
          }
        })}
      </main>
    </>
  );
};

export default Main;
